/**
 * PavedCalculationOrchestrator - Phase 2 Business Logic Extraction
 *
 * Centralizes all business logic previously embedded in DesignCondition.tsx.
 * Coordinates service calls, handles data transformation, and manages error scenarios.
 *
 * Architecture: Service Layer → Business Logic Layer → UI Layer
 * Performance: Eliminates complex logic from UI components for better separation of concerns
 */

import { finiteOr, logDebug, logError, LogTag } from '../utils/utils';
import { materialDatabaseService } from './MaterialDatabaseService';
import { DrainageCoefficientService } from './DrainageCoefficientService';
import { esalThicknessService } from './EsalThicknessService';
import { CBRCalculationService } from './CBRCalculationService';
import { ILayerAnalysisInputs } from '../types/CalculationTypes';
import { IPavedData } from '../api/IApiResponse';
import { DEFAULT_MATERIAL_PROPERTIES, AASHTO_CONSTANTS } from '../constants/AASHTOConstants';

/**
 * Business Logic Orchestrator for Paved Calculations
 *
 * Extracted from DesignCondition.tsx to centralize:
 * - Form data transformation logic
 * - Dynamic coefficient calculations
 * - Service coordination
 * - Error handling and fallback strategies
 */
export class PavedCalculationOrchestrator {
    constructor(private materialService = materialDatabaseService) {}

    /**
     * Orchestrate complete paved calculation from form data to analysis inputs
     *
     * @param formData - IPavedData from the form
     * @returns Promise<ILayerAnalysisInputs> - Structured inputs for AASHTO calculation
     */
    async orchestrateCalculation(formData: IPavedData): Promise<ILayerAnalysisInputs> {
        try {
            logDebug('PavedCalculationOrchestrator', 'Starting calculation orchestration with form data:', formData, LogTag.AASHTO_CALC);

            // Extract form sections for easier access
            const { roadInfo, trafficLoad, drainageDesign, surface, baseCourse, subbaseCourse, subgrade } = formData;

            // Extract material IDs with fallback to Excel defaults - handle both IDs and display names
            const surfaceMaterialId = surface?.surfaceMaterial || 'Asphalt Concrete (Dense-Graded)';

            // For base material: try as ID first, if not found try as display name
            let baseMaterialId = baseCourse?.baseMaterial || this.materialService.getDefaultBaseMaterial().id;
            if (!this.materialService.getBaseMaterialById(baseMaterialId)) {
                // If not found by ID, try finding by display name
                const foundBaseId = this.materialService.getBaseMaterialIdByName(baseMaterialId);
                if (foundBaseId) {
                    baseMaterialId = foundBaseId;
                } else {
                    // Fallback to default ID
                    baseMaterialId = this.materialService.getDefaultBaseMaterial().id;
                }
            }

            // For subbase material: try as ID first, if not found try as display name
            let subbaseMaterialId = subbaseCourse?.subbaseMaterial || 'crushed_gravel_subbase';
            if (!this.materialService.getSubbaseMaterialById(subbaseMaterialId)) {
                // If not found by ID, try finding by display name
                const foundSubbaseId = this.materialService.getSubbaseMaterialIdByName(subbaseMaterialId);
                if (foundSubbaseId) {
                    subbaseMaterialId = foundSubbaseId;
                } else {
                    // Fallback to crushed gravel (which is the material causing the issue)
                    subbaseMaterialId = 'crushed_gravel_subbase';
                }
            }

            logDebug(
                'PavedCalculationOrchestrator',
                'Material IDs extracted and validated:',
                {
                    surface: surfaceMaterialId,
                    base: baseMaterialId,
                    subbase: subbaseMaterialId,
                    originalBase: baseCourse?.baseMaterial,
                    originalSubbase: subbaseCourse?.subbaseMaterial,
                    roadType: trafficLoad?.roadType || 'Urban',
                    esalCalculationMode: trafficLoad?.esalCalculationMode || 'manual',
                },
                LogTag.MATERIAL_DB
            );

            // Get material data from MaterialDatabaseService
            const baseMaterial = this.materialService.getBaseMaterialById(baseMaterialId);
            const subbaseMaterial = this.materialService.getSubbaseMaterialById(subbaseMaterialId);

            const subgradeEv2 = subgrade?.ev2Subgrade || 50; // Excel G39 default

            // Calculate dynamic CBR values using MaterialDatabaseService with IDs
            const dynamicCBR = this.materialService.getDynamicCBRValues(
                baseMaterialId,
                subbaseMaterialId,
                subgradeEv2,
                'Ev2' // Use Ev2 method as per Excel default
            );

            // Calculate subgrade CBR based on user's input method choice
            const subgradeCBR = this.calculateSubgradeCBR((subgrade as unknown as Record<string, unknown>) || {}, (roadInfo as unknown as Record<string, unknown>) || {});

            logDebug(
                'PavedCalculationOrchestrator',
                'CBR values calculated:',
                {
                    static: {
                        baseCbr: baseCourse?.cbrBaseCourse,
                        subbaseCbr: subbaseCourse?.cbrSubbaseCourse,
                        subgradeCbr: subgrade?.cbrSubgrade,
                    },
                    dynamic: dynamicCBR,
                    calculated: { subgradeCBR },
                    note: 'Using dynamic Excel-calculated CBR values with user-selected subgrade input method',
                },
                LogTag.AASHTO_CALC
            );

            // Calculate dynamic drainage coefficients
            const drainageCoefficients = await this.calculateDrainageCoefficients(
                (roadInfo as unknown as Record<string, unknown>) || {},
                (baseMaterial as unknown as Record<string, unknown>) || {},
                (drainageDesign as unknown as Record<string, unknown>) || {},
                (baseCourse as unknown as Record<string, unknown>) || {},
                (subbaseCourse as unknown as Record<string, unknown>) || {}
            );

            // Debug: Log geogrid IDs being passed

            // Calculate dynamic minimum thicknesses based on ESAL (Excel VLOOKUP logic)
            const designESAL = trafficLoad?.esalFinalValue || 0;
            const asphaltMinThickness = designESAL > 0
                ? esalThicknessService.getAsphaltConcreteMinThickness(designESAL)
                : null;
            const baseMinThickness = designESAL > 0
                ? esalThicknessService.getAggregateBaseMinThickness(designESAL)
                : null;

            logDebug(
                'PavedCalculationOrchestrator',
                `Dynamic minimum thickness lookup for ESAL=${designESAL}:`,
                {
                    esal: designESAL,
                    asphaltMin_m: asphaltMinThickness?.minThickness_m,
                    asphaltMin_inches: asphaltMinThickness?.minThickness_inches,
                    baseMin_m: baseMinThickness?.minThickness_m,
                    baseMin_inches: baseMinThickness?.minThickness_inches,
                    esalRange: asphaltMinThickness?.esalRange,
                    trafficCategory: designESAL > 0 ? esalThicknessService.getTrafficCategory(designESAL) : 'Unknown',
                },
                LogTag.AASHTO_CALC
            );

            const userAsphaltMin = typeof surface?.minThickness === 'number' && Number.isFinite(surface.minThickness) ? surface.minThickness : 0;
            const userBaseMin = typeof baseCourse?.minThickness === 'number' && Number.isFinite(baseCourse.minThickness) ? baseCourse.minThickness : AASHTO_CONSTANTS.MIN_THICKNESS.BASE;

            // Build the complete ILayerAnalysisInputs structure
            const inputs: ILayerAnalysisInputs = {
                w18: designESAL,
                reliability: trafficLoad?.reliabilityLevel || 0,
                so: trafficLoad?.combinedStandardError || 0,
                deltaPsi: (trafficLoad?.initialServiceIndex || 4.5) - finiteOr(trafficLoad?.terminalSurfaceIndex, 2.5),

                // Layer modulus calculations using dynamic CBR values
                baseCourse: {
                    modulus: CBRCalculationService.calculateMrInPsi(dynamicCBR.baseCbr), // Use dynamic Excel-calculated CBR
                },
                subbaseCourse: {
                    modulus: CBRCalculationService.calculateMrInPsi(dynamicCBR.subbaseCbr), // Use dynamic Excel-calculated CBR
                },
                subgrade: {
                    modulus: CBRCalculationService.calculateMrInPsi(subgradeCBR), // Use calculated CBR based on user's input method choice
                },

                // Stabilised course data for geogrid reinforcement calculations
                stabilisedBaseCourse: {
                    modulus: CBRCalculationService.calculateMrInPsi(dynamicCBR.baseCbr), // Same modulus as unstabilised
                },
                stabilisedSubbaseCourse: {
                    modulus: CBRCalculationService.calculateMrInPsi(dynamicCBR.subbaseCbr), // Same modulus as unstabilised
                },

                // Material IDs for dynamic coefficient lookup (use actual form selections)
                materialIds: {
                    surface: surfaceMaterialId, // Use actual selected surface material from form
                    base: baseMaterialId, // Use actual selected base material from form
                    subbase: subbaseMaterialId, // Use actual selected subbase material from form
                },

                // Geogrid IDs for stabilised layer calculations (dynamic from form)
                geogridIds: {
                    base: baseCourse?.baseGeogrid && baseCourse.baseGeogrid !== '' ? baseCourse.baseGeogrid : 'none_selected', // Handle empty string as none_selected
                    subbase: subbaseCourse?.subbaseGeogrid && subbaseCourse.subbaseGeogrid !== '' ? subbaseCourse.subbaseGeogrid : 'none_selected', // Handle empty string as none_selected
                },

                // Include roadInfo for dynamic drainage calculation
                roadInfo: {
                    carriagewayWidth: (() => {
                        const width = roadInfo?.carriagewayWidth || 7.0;
                        return width;
                    })(),
                    longitudinalSlope: finiteOr(roadInfo?.longitudinalSlope, 2) / 100, // Convert percentage to decimal
                    transversalSlope: (roadInfo?.transversalSlope || 2.5) / 100, // Convert percentage to decimal
                },

                // Complete traffic load data (including ESAL calculation metadata)
                trafficLoad: {
                    functionalClassification: trafficLoad?.functionalClassification || 'Interstate and Other Freeways',
                    roadType: trafficLoad?.roadType || 'Urban',
                    reliabilityLevel: trafficLoad?.reliabilityLevel || 95, // Same as 'reliability' field above
                    combinedStandardError: finiteOr(trafficLoad?.combinedStandardError, 0.5), // Same as 'so' field above
                    initialServiceIndex: trafficLoad?.initialServiceIndex || 4.5,
                    terminalSurfaceIndex: finiteOr(trafficLoad?.terminalSurfaceIndex, 2.5),
                    // Note: esalCalculationMode is used for UI logic, not calculation service
                },

                // Dynamic drainage coefficients
                drainageCoefficients,

                // Minimum thicknesses: only the asphalt uses the ESAL minimum (CALCULATION K7 = VLOOKUP(ESAL, DATABASE I7:J12));
                // the base uses the user minimum alone, K16 = VLOOKUP(ESAL, DATABASE I16:J21) is only the suggested minimum
                minimumThicknesses: {
                    // Excel CALCULATION!Q5: MAX(DATABASE!K7, Design_LCR!G8); a blank or invalid user value is skipped like an empty cell
                    surface: Math.max(asphaltMinThickness?.minThickness_m ?? 0, userAsphaltMin) || AASHTO_CONSTANTS.MIN_THICKNESS.SURFACE,
                    // Excel CALCULATION!Q6/S6 use only the user minimum Design_LCR!G14; DATABASE!K16 is just the suggested minimum (G13)
                    base: userBaseMin,
                    // Subbase: Excel doesn't have VLOOKUP table for subbase, use form value or default
                    subbase: subbaseCourse?.minThickness || 0.15,
                },

                // Include drainage design data for Excel-compatible coefficient calculation
                drainageDesign: {
                    drainageBelowBase: drainageDesign?.drainageBelowBase || 'no',
                    drainageBelowSubbase: drainageDesign?.drainageBelowSubbase || 'no',
                    referenceTimeForDrainage: drainageDesign?.referenceTimeForDrainage || 'hours',
                    pavementMoistureExposure: drainageDesign?.pavementMoistureExposure || '2_25',
                    // Drainage product types are now fully supported by DrainageCoefficientService
                    baseDrainageType: drainageDesign?.baseDrainageType, // 'macdrain_w_1351' | 'geogrid_only' | 'geocomposite_drainage'
                    subbaseDrainageType: drainageDesign?.subbaseDrainageType, // 'multicomposito' | 'geogrid_only' | 'geocomposite_drainage'
                },

                // Subgrade input method and raw values for reference
                subgradeInput: {
                    inputMethod: (roadInfo?.subgradeInputMethod || 'cbr') as 'cbr' | 'ev2',
                    cbrValue: roadInfo?.subgradeInputMethod === 'cbr' ? subgrade?.cbrSubgrade : undefined,
                    ev2Value: roadInfo?.subgradeInputMethod === 'ev2' ? subgrade?.ev2Subgrade : undefined,
                },
            };

            logDebug('PavedCalculationOrchestrator', 'Calculation inputs orchestrated successfully:', inputs, LogTag.AASHTO_CALC);
            return inputs;
        } catch (error) {
            logError('PavedCalculationOrchestrator', 'Error orchestrating calculation:', error, LogTag.ERROR_HANDLING);
            throw new Error(`Failed to orchestrate paved calculation: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Calculate subgrade CBR based on user's input method choice
     * Extracted from DesignCondition.tsx lines 220-240
     */
    private calculateSubgradeCBR(subgrade: Record<string, unknown>, roadInfo: Record<string, unknown>): number {
        const inputMethod = roadInfo?.subgradeInputMethod || 'cbr'; // Default to CBR method

        if (inputMethod === 'ev2') {
            // Convert Ev2 to CBR using Excel formula: CBR = Ev2/10 (if Ev2<=50) or CBR = (Ev2/17.6)^(1/0.64) (if Ev2>50)
            const ev2Value = (subgrade?.ev2Subgrade as number) || 50;
            const subgradeCBR = ev2Value <= 50 ? ev2Value / 10 : Math.pow(ev2Value / 17.621, 1 / 0.64);
            logDebug('PavedCalculationOrchestrator', `Converting Ev2=${ev2Value} to CBR=${subgradeCBR}`, undefined, LogTag.AASHTO_CALC);
            return subgradeCBR;
        } else {
            // Use CBR directly from form
            const subgradeCBR = (subgrade?.cbrSubgrade as number) || 2; // Default to CBR=2 if not provided
            logDebug('PavedCalculationOrchestrator', `Using direct CBR=${subgradeCBR} from form`, undefined, LogTag.AASHTO_CALC);
            return subgradeCBR;
        }
    }

    /**
     * Calculate dynamic drainage coefficients matching Excel G8/H8 logic
     * Business Logic Layer: Coordinates DrainageCoefficientService
     *
     * EXCEL LOGIC:
     * - G8 (unstabilised m3) = K25 (DRAINAGE_m table lookup, always 0.4 for Very poor quality)
     * - H8 (stabilised m3) = IF(C6="No", G8, K29) - conditional on subbase drainage
     *
     * NOTE: K63 formula exists in Excel (IF(C4="Yes",1,K25)) but is used ONLY in Design_LCR sheet
     * for other calculations (K65, K71), NOT in the main thickness calculations Q7/S7 which use G8/H8
     */
    private async calculateDrainageCoefficients(
        roadInfo: Record<string, unknown>,
        baseMaterial: Record<string, unknown>,
        drainageDesign: Record<string, unknown>,
        baseCourse: Record<string, unknown>,
        subbaseCourse: Record<string, unknown>
    ) {
        try {
            // Check drainage configuration first (Excel cell references)
            const hasBaseDrainage = drainageDesign?.drainageBelowBase === 'yes'; // Excel C4
            const hasSubbaseDrainage = drainageDesign?.drainageBelowSubbase === 'yes'; // Excel C5

            // Prepare parameters for DrainageCoefficientService call
            const baseMinThickness = (baseCourse?.minThickness as number) || AASHTO_CONSTANTS.MIN_THICKNESS.BASE; // From form or Excel G14 default (0.15m)
            const subbaseMinThickness = (subbaseCourse?.minThickness as number) || AASHTO_CONSTANTS.MIN_THICKNESS.SUBBASE; // From form or Excel G27 default (0.15m)

            // Excel methodology: use minThickness * 1.5 to avoid circular references
            const baseDrainageThickness = baseMinThickness * 1.5;
            const subbaseDrainageThickness = subbaseMinThickness * 1.5;

            // Get subbase material properties for the service call
            let subbaseMaterialId = (subbaseCourse?.subbaseMaterial as string) || 'crushed_gravel_subbase';
            if (!this.materialService.getSubbaseMaterialById(subbaseMaterialId)) {
                const foundSubbaseId = this.materialService.getSubbaseMaterialIdByName(subbaseMaterialId);
                if (foundSubbaseId) {
                    subbaseMaterialId = foundSubbaseId;
                } else {
                    subbaseMaterialId = 'crushed_gravel_subbase';
                }
            }
            const subbaseMaterial = this.materialService.getSubbaseMaterialById(subbaseMaterialId);

            // DELEGATE to DrainageCoefficientService for K25 calculation (always performed)
            const dynamicCoefficients = DrainageCoefficientService.calculateDrainageCoefficients(
                (roadInfo?.carriagewayWidth as number) || 7.0,
                finiteOr(roadInfo?.longitudinalSlope, 2) / 100, // Convert percentage to decimal
                ((roadInfo?.transversalSlope as number) || 2.5) / 100, // Convert percentage to decimal
                baseDrainageThickness, // Base drainage thickness
                subbaseDrainageThickness, // Subbase drainage thickness
                (baseMaterial?.effectivePorosity as number) || DEFAULT_MATERIAL_PROPERTIES.BASE.EFFECTIVE_POROSITY, // Excel DATABASE.txt H91 = 0.32
                (baseMaterial?.permeabilityCoefficient as number) || DEFAULT_MATERIAL_PROPERTIES.BASE.PERMEABILITY_COEFF, // Excel DATABASE.txt I91 = 8.46 m/day
                (subbaseMaterial?.effectivePorosity as number) || DEFAULT_MATERIAL_PROPERTIES.SUBBASE.EFFECTIVE_POROSITY, // Excel DATABASE.txt H98 = 0.2
                (subbaseMaterial?.permeabilityCoefficient as number) || DEFAULT_MATERIAL_PROPERTIES.SUBBASE.PERMEABILITY_COEFF, // Excel DATABASE.txt I98 = 0.0846 m/day
                (drainageDesign?.pavementMoistureExposure as string)?.replace('_', ' - ') + ' %' || '2 - 25 %',
                drainageDesign?.drainageBelowBase === 'yes' ? (drainageDesign?.baseDrainageType as string) : undefined,
                drainageDesign?.drainageBelowSubbase === 'yes' ? (drainageDesign?.subbaseDrainageType as string) : undefined
            );

            // Return coefficients from DrainageCoefficientService (already implements Excel G8/H8 logic)
            logDebug(
                'PavedCalculationOrchestrator',
                'Drainage coefficients orchestrated with Excel G8/H8 logic:',
                {
                    serviceDelegation: 'DrainageCoefficientService.calculateDrainageCoefficients()',
                    serviceResult: dynamicCoefficients,
                    excelLogic: {
                        g8_unstabilised: dynamicCoefficients.m3,
                        h8_stabilised: dynamicCoefficients.m3_stabilised,
                        h8_condition: hasSubbaseDrainage ? 'C6="Yes" → K29 (stabilised table)' : 'C6="No" → G8 (unstabilised table)',
                    },
                    finalResult: {
                        base: dynamicCoefficients.m2,
                        subbase: dynamicCoefficients.m3,
                        baseStabilised: dynamicCoefficients.m2_stabilised,
                        subbaseStabilised: dynamicCoefficients.m3_stabilised,
                    },
                    note: 'Service already implements correct Excel logic: G8 for unstabilised, H8 conditional for stabilised',
                },
                LogTag.AASHTO_CALC
            );

            // Return coefficients directly from service (matches Excel CALCULATION.txt Q7/S7 formulas)
            return {
                base: dynamicCoefficients.m2, // m2 - Excel G7 (base unstabilised)
                subbase: dynamicCoefficients.m3, // m3 - Excel G8 = K25 (subbase unstabilised from DRAINAGE_m table)
                baseStabilised: dynamicCoefficients.m2_stabilised, // m2_stabilised - Excel H7 (base stabilised)
                subbaseStabilised: dynamicCoefficients.m3_stabilised, // m3_stabilised - Excel H8 = IF(C6="No", G8, K29)
            };
        } catch (error) {
            logError('PavedCalculationOrchestrator', 'Error calculating dynamic drainage coefficients, using Excel-compatible fallback values:', error, LogTag.ERROR_HANDLING);

            // Fallback to Excel-compatible default values
            const hasSubbaseDrainage = drainageDesign?.drainageBelowSubbase === 'yes';

            // Use Excel G7/G8 default values (not K63!)
            return {
                base: 0.8, // m2 - Fair drainage (Excel G7 default)
                subbase: 0.4, // m3 - Very poor drainage (Excel G8 = K25 default)
                baseStabilised: 1.0, // m2_stabilised - Good drainage for stabilised base (Excel H7 default)
                subbaseStabilised: hasSubbaseDrainage ? 1.15 : 0.4, // Excel H8: IF(C6="No", G8=0.4, K29=1.15)
            };
        }
    }
}

// Export singleton instance for consistent usage
export const pavedCalculationOrchestrator = new PavedCalculationOrchestrator();
